import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLBox} from '@github-ui/safe-html/VerifiedHTML'
import {FormControl} from '@primer/react'
import {clsx} from 'clsx'
import {type ReactNode, useId} from 'react'

import styles from './ElementWrapper.module.css'

type ElementWrapperProps = {
  /** Text used as label */
  label: string
  /** Forces user input and displays a visual required element */
  required?: boolean | null
  /** Text used as the field description */
  description?: string | null
  /** Validation message to display */
  validationResult?: string | undefined
  /** Child function that passes the label and description ids for accessibility */
  children: (renderProps: ElementWrapperRenderProps) => ReactNode
  /** Additional class names to apply to the wrapper */
  className?: string
}

type ElementWrapperRenderProps = {
  /** Id of the label element to attach via the `aria-labelledby` attribute */
  labelId: string
  /** Ids of the description and validation elements to attach via the `aria-describedby` attribute */
  descriptionIds: string
}

export function ElementWrapper({
  children,
  label,
  required,
  description,
  validationResult,
  className,
}: ElementWrapperProps) {
  const labelId = useId()
  const descriptionId = useId()
  const validationId = useId()
  // We can remove the outter box and shift the `children` rendering back inside the `FormControl` once
  // https://github.com/github/collaboration-workflows-flex/issues/178 is fixed.
  // This is a temporary work around to unblock the `MarkdownEditor` toolbar from not working.
  return (
    <div className={clsx(styles.elementWrapperContainer, className)}>
      <FormControl required={required ?? false}>
        <FormControl.Label
          id={labelId}
          className={clsx(styles.elementLabel, description && styles.elementLabelWithDescription)}
        >
          {label}
        </FormControl.Label>
        {description && (
          <FormControl.Caption id={descriptionId} className={styles.elementDescription}>
            <VerifiedHTMLBox
              // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string
              html={description as SafeHTMLString}
              className="markdown-body note text-small mb-2"
            />
          </FormControl.Caption>
        )}
      </FormControl>
      {children({
        labelId,
        descriptionIds: `${descriptionId} ${validationId}`,
      })}
      {validationResult && (
        <FormControl.Validation id={validationId} variant="error" className={styles.validationMessage}>
          {validationResult}
        </FormControl.Validation>
      )}
    </div>
  )
}

try{ ElementWrapper.displayName ||= 'ElementWrapper' } catch {}